import React, { useEffect } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import MainPage from '../pages/mainPage/MainPage';
import { NotFound } from '../pages/notFound/notFound';
import ResolveDefaultPage from './ResolveDefaultPage';
import ResolveZohoCrmAccount from './ResolveZohoCrmAccount';
import { Guard } from '../components/guard/guard';
import { rootRoutes } from './Routes.constants';
import { paths } from '../constants/paths';
import Contractor from '../features/contractor/Contractor';
import { Risks } from '../pages/risks/risks';
import GlobalTracker from '../features/globalTracker/GlobalTracker';
import { TimeAndPeople } from '../pages/timeAndPeople/timeAndPeople';
import { Alert } from '../features/alerts/Alert';
import { ViewAlert } from '../features/alerts/ViewAlert';
import { Invoices } from '../features/invoices/Invoices';
import { SowAdmin } from '../features/sow-admin/SowAdmin';
import { MarginDetails } from '../features/budgets/MarginDetails';
import { AccountDetails } from '../pages/myPortfolio/AccountDetails';
import { RecentOperationalChanges } from '../features/RecentSowChanges/RecentOperationalChanges';
import { AccountRevenues } from '../features/quarterly-revenue/AccountRevenues';

const AccountSowManagement = () => {
  const { accountId, projectId } = useParams();

  return (
    <SowAdmin
      accountIdFilter={accountId}
      projectIdFilter={projectId}
      embedded
    />
  );
};

const CustomRoutes: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Redirecting to /portfolio from /accounts
  useEffect(() => {
    const pathname = location.pathname;
    if (pathname.includes(paths.accounts)) {
      const newPath = pathname.replace(paths.accounts, paths.portfolio);
      navigate(newPath);
    }
  }, [location, navigate]);

  return (
    <Routes>
      <Route path={`${paths.rockyBonuses}/:userId`} element={<Contractor />} />

      <Route path="/" element={<MainPage />}>
        {rootRoutes.map(
          ({
            path,
            permissions,
            emails,
            element: Element,
            requireAllPermissions,
          }) => (
            <Route
              key={path}
              path={path}
              element={
                <Guard
                  permissions={permissions}
                  emails={emails}
                  requireAllPermissions={requireAllPermissions}
                >
                  <Element />
                </Guard>
              }
            />
          ),
        )}
        <Route
          path={`${paths.portfolio}/zoho/:zohoCrmId`}
          element={
            <Guard permissions={['hasAccounts']}>
              <ResolveZohoCrmAccount />
            </Guard>
          }
        />
        <Route
          path={`${paths.portfolio}/:accountId`}
          element={
            <Guard permissions={['hasAccounts']}>
              <AccountDetails />
            </Guard>
          }
        >
          {[
            {
              linkTo: '',
              element: <Navigate to={paths.ratesAndMargins} />,
            },
            {
              linkTo: paths.ratesAndMargins,
              element: <MarginDetails />,
            },
            {
              linkTo: paths.sowManagement,
              element: (
                <Guard
                  permissions={[
                    'isFinance',
                    'isFinanceManager',
                    'hasAccounts',
                    'isCompensationManager',
                  ]}
                  requireAllPermissions={false}
                >
                  <AccountSowManagement />
                </Guard>
              ),
            },
            {
              linkTo: paths.forecast,
              element: <AccountRevenues />,
            },
            {
              linkTo: paths.timeAndPeople,
              element: <TimeAndPeople />,
            },
            {
              linkTo: paths.projectStatus,
              element: <GlobalTracker />,
            },
            {
              linkTo: paths.risks,
              element: <Risks />,
            },
            {
              linkTo: paths.invoices,
              element: <Invoices />,
            },
          ].map(({ linkTo, element }) => (
            <React.Fragment key={linkTo}>
              <Route path={linkTo} element={element} />
              <Route path={`:projectId/${linkTo}`} element={element} />
            </React.Fragment>
          ))}
        </Route>
        <Route
          path={`${paths.sowAdmin}`}
          element={
            <Guard
              permissions={[
                'isFinance',
                'isFinanceManager',
                'hasAccounts',
                'isCompensationManager',
              ]}
              requireAllPermissions={false}
            >
              <SowAdmin />
            </Guard>
          }
        />
        <Route
          path={`${paths.recentSowChanges}`}
          element={
            <Guard
              permissions={[
                'isFinance',
                'isFinanceManager',
                'hasAccounts',
                'isCompensationManager',
              ]}
              requireAllPermissions={false}
            >
              <RecentOperationalChanges />
            </Guard>
          }
        />
        <Route
          path={`:accountId/${paths.alert}/create`}
          element={
            <Guard permissions={['hasAccounts', 'isCompensationManager']}>
              <Alert isEdit={false} />
            </Guard>
          }
        />
        <Route
          path={`:accountId/${paths.alert}/:alertId/edit`}
          element={
            <Guard permissions={['hasAccounts', 'isCompensationManager']}>
              <Alert isEdit={true} />
            </Guard>
          }
        />
        <Route
          path={`:accountId/${paths.alert}/:alertId/view`}
          element={
            <Guard permissions={['hasAccounts', 'isCompensationManager']}>
              <ViewAlert />
            </Guard>
          }
        />
        <Route index element={<ResolveDefaultPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default CustomRoutes;
